import { IAuditLogEntry } from '@dadohr/shared/domainTypes/auditLog';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  RowsRendererScrollContainer,
  ROW_HEIGHTS
} from '../../../../../../../components/GroupableList';
import { Loader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import {
  formatDatePrecise,
  toMoment
} from '../../../../../../../services/time';
import { JsonDialogInner } from '../../../../../../components/Json';
import { useAuditLogForDoc } from '../../../../../../services/auditLog';
import { toFirestoreConsoleFromDoc } from '../../../../../../services/firebase';

type ColumnName = 'type' | 'executor' | 'at' | 'actions';
type D = Doc<IAuditLogEntry>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspectRaw',
        label: 'Inspect (raw)...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'type',
    head: () => 'Type ',
    cell: (d) => d.data.type,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'executor',
    head: () => 'Executor',
    cell: (d) => d.data.executor,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'at',
    head: () => 'at',
    cell: (d) => formatDatePrecise(toMoment(d.data.at)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  at: {
    key: 'at',
    items: { sort: (d) => d.data.at.toMillis(), dir: 'desc' }
  }
};

const HEIGHT = 300;

export const AuditLog = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const [ds, loading, error] = useAuditLogForDoc(tpl, 50);
  useErrorLogger('audit log', error);
  return (
    <Section>
      <CanvasBar>
        <div>Audit Log</div>
        <Link
          to={`/orgs/${tpl.data.orgId}/experience-templates/${tpl.id}/audit-log`}
        >
          Go to Audit Log page
        </Link>
      </CanvasBar>

      {loading && <Loader height={HEIGHT} />}
      {ds && (
        <RowsRendererScrollContainer height={HEIGHT}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={ds}
            rowToKey={ID_TO_ROW_KEY}
            sorter={SORTERS.at}
            sortDirection="desc"
            renderHead={true}
            headProps={{ sticky: true }}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={undefined}
          />
        </RowsRendererScrollContainer>
      )}
    </Section>
  );
};
